import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const NotFound = () => {
  const mountRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene Setup
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000000, 0.001);

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 50;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);

    // Particles - The Void
    const geometry = new THREE.BufferGeometry();
    const count = 3000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 100;
      // Purple/Blue/Cyan gradient
      colors[i] = Math.random();
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      sizeAttenuation: true
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // Floating Geometric Shapes (Debris)
    const debrisGroup = new THREE.Group();
    const debrisGeom = new THREE.TetrahedronGeometry(1, 0);
    const debrisMat = new THREE.MeshPhongMaterial({
      color: 0x6366f1,
      shininess: 100,
      flatShading: true,
      transparent: true,
      opacity: 0.6
    });

    for (let i = 0; i < 20; i++) {
      const mesh = new THREE.Mesh(debrisGeom, debrisMat);
      mesh.position.set(
        (Math.random() - 0.5) * 60,
        (Math.random() - 0.5) * 60,
        (Math.random() - 0.5) * 60
      );
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      const scale = Math.random() * 2;
      mesh.scale.set(scale, scale, scale);
      debrisGroup.add(mesh);
    }
    scene.add(debrisGroup);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x4361ee, 2, 50);
    pointLight.position.set(10, 10, 10);
    scene.add(pointLight);

    const redLight = new THREE.PointLight(0xff0055, 2, 50);
    redLight.position.set(-10, -10, 10);
    scene.add(redLight);

    // Animation Loop
    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (event) => {
      mouseX = (event.clientX - window.innerWidth / 2) * 0.05;
      mouseY = (event.clientY - window.innerHeight / 2) * 0.05;
    };

    document.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      requestAnimationFrame(animate);

      particles.rotation.y += 0.001;
      particles.rotation.x += 0.0005;

      debrisGroup.rotation.y -= 0.002;
      debrisGroup.rotation.x -= 0.001;

      // Gentle float for debris
      debrisGroup.children.forEach((child, i) => {
        child.rotation.x += 0.01;
        child.rotation.y += 0.01;
        child.position.y += Math.sin(Date.now() * 0.001 + i) * 0.02;
      });

      // Camera sway
      camera.position.x += (mouseX - camera.position.x) * 0.05;
      camera.position.y += (-mouseY - camera.position.y) * 0.05;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate();

    // Resize Handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousemove', handleMouseMove);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      debrisGeom.dispose();
      debrisMat.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <Container>
      <CanvasContainer ref={mountRef} />

      <ContentWrapper>
        <GlitchText
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          404
        </GlitchText>

        <SubText
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          SYSTEM MALFUNCTION // COORDINATES INVALID
        </SubText>

        <Description
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          The page you are looking for has drifted into the digital void.
        </Description>

        <HomeButton
          whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(67, 97, 238, 0.5)" }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          onClick={() => navigate('/')}
        >
          <ButtonIcon className="fas fa-satellite-dish" />
          RETURN TO BASE
        </HomeButton>
      </ContentWrapper>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  z-index: 9999;
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  max-width: 600px;
  width: 90%;
`;

const GlitchText = styled(motion.h1)`
  font-size: 8rem;
  font-weight: 900;
  color: #fff;
  margin: 0;
  line-height: 1;
  letter-spacing: -5px;
  text-shadow: 2px 2px 0px #ff0055, -2px -2px 0px #4361ee;
  position: relative;
  
  &::before, &::after {
    content: '404';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #000;
  }
  
  &::before {
    left: 2px;
    text-shadow: -1px 0 #ff0055;
    clip: rect(24px, 550px, 90px, 0);
    animation: glitch-anim-2 3s infinite linear alternate-reverse;
  }
  
  &::after {
    left: -2px;
    text-shadow: -1px 0 #4361ee;
    clip: rect(85px, 550px, 140px, 0);
    animation: glitch-anim 2.5s infinite linear alternate-reverse;
  }

  @keyframes glitch-anim {
    0% { clip: rect(12px, 9999px, 32px, 0); }
    20% { clip: rect(84px, 9999px, 100px, 0); }
    40% { clip: rect(12px, 9999px, 6px, 0); }
    60% { clip: rect(54px, 9999px, 42px, 0); }
    80% { clip: rect(24px, 9999px, 86px, 0); }
    100% { clip: rect(62px, 9999px, 12px, 0); }
  }

  @keyframes glitch-anim-2 {
    0% { clip: rect(62px, 9999px, 12px, 0); }
    20% { clip: rect(24px, 9999px, 86px, 0); }
    40% { clip: rect(54px, 9999px, 42px, 0); }
    60% { clip: rect(12px, 9999px, 6px, 0); }
    80% { clip: rect(84px, 9999px, 100px, 0); }
    100% { clip: rect(12px, 9999px, 32px, 0); }
  }
  
  @media (max-width: 768px) {
    font-size: 5rem;
  }
`;

const SubText = styled(motion.h2)`
  font-size: 1.2rem;
  color: #4cc9f0;
  margin-top: 1rem;
  letter-spacing: 3px;
  font-weight: 600;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    letter-spacing: 1px;
  }
`;

const Description = styled(motion.p)`
  font-size: 1rem;
  color: #94a3b8;
  margin-top: 1rem;
  margin-bottom: 2.5rem;
  line-height: 1.6;
`;

const HomeButton = styled(motion.button)`
  background: linear-gradient(135deg, #4361ee, #3a0ca3);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 10px 20px -10px rgba(67, 97, 238, 0.5);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const ButtonIcon = styled.i`
  font-size: 1.1rem;
`;

export default NotFound;
